import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, MessageCircle, Send } from "lucide-react";
import { SITE, buildWhatsAppLink } from "../../lib/site";
import { Reveal } from "../Reveal";

export const Contact = () => {
  const [form, setForm] = useState({ nume: "", telefon: "", mesaj: "" });
  const [gdpr, setGdpr] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!gdpr) return;
    const text = `Bună ziua! Sunt ${form.nume || "un potențial elev"}.
Telefon: ${form.telefon || "-"}
Mesaj: ${form.mesaj || "Doresc detalii despre înscrierea la categoria B."}`;
    window.open(buildWhatsAppLink(text), "_blank");
  };

  const directory = [
    { icon: MapPin, label: "Locație", value: SITE.address, href: `https://maps.google.com/?q=${encodeURIComponent(SITE.address)}` },
    { icon: Clock, label: "Program", value: SITE.schedule },
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="max-w-2xl lg:ml-[8.333%]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1565C0]">Contact</p>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            Hai să pornim la drum împreună
          </h2>
          <p className="mt-4 text-slate-600 text-lg">Completează formularul și trimite-l direct pe WhatsApp — îți răspundem rapid.</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Directorul oficial — datele paginii de semnătură */}
          <Reveal className="lg:col-span-5 lg:col-start-1">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Telefon</div>
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="contact-info-Telefon"
                className="mt-2 block font-display font-extrabold text-4xl sm:text-5xl text-slate-900 hover:text-[#1565C0] transition-colors tracking-tight"
              >
                {SITE.phoneDisplay}
              </a>

              <div className="mt-10 border-t border-slate-200">
                {directory.map((it) => {
                  const Comp = it.href ? "a" : "div";
                  return (
                    <Comp
                      key={it.label}
                      {...(it.href ? { href: it.href, target: "_blank", rel: "noopener noreferrer" } : {})}
                      data-testid={`contact-info-${it.label}`}
                      className="group flex items-start justify-between gap-6 py-5 border-b border-slate-200"
                    >
                      <span className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400 pt-1">
                        <it.icon size={15} className="text-[#1565C0]" /> {it.label}
                      </span>
                      <span className="text-right font-medium text-slate-900 text-[15px] group-hover:text-[#1565C0] transition-colors">{it.value}</span>
                    </Comp>
                  );
                })}
              </div>

              <a
                href={buildWhatsAppLink("Bună ziua! Doresc să mă înscriu la categoria B.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="mt-8 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-2xl px-6 py-4 font-semibold transition-all"
              >
                <MessageCircle size={20} /> Scrie-ne direct pe WhatsApp
              </a>

              <div className="relative mt-6 rounded-3xl overflow-hidden border border-slate-200 h-[180px] bg-[#EAF2FB]" data-testid="stylized-map">
                <svg viewBox="0 0 400 240" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
                  <rect width="400" height="240" fill="#EAF2FB" />
                  <g fill="#D7E7FB">
                    <rect x="26" y="28" width="74" height="48" rx="7" />
                    <rect x="118" y="18" width="92" height="40" rx="7" />
                    <rect x="252" y="34" width="64" height="60" rx="7" />
                    <rect x="330" y="60" width="52" height="46" rx="7" />
                    <rect x="36" y="150" width="82" height="62" rx="7" />
                    <rect x="176" y="152" width="72" height="60" rx="7" />
                    <rect x="292" y="156" width="80" height="56" rx="7" />
                  </g>
                  <g stroke="#B7D3F4" strokeWidth="9" fill="none" strokeLinecap="round">
                    <path d="M0 112 H400" />
                    <path d="M150 0 V240" />
                    <path d="M-10 205 Q 200 120 410 185" />
                  </g>
                  <g stroke="#ffffff" strokeWidth="2.5" strokeDasharray="7 9" fill="none">
                    <path d="M0 112 H400" />
                    <path d="M150 0 V240" />
                  </g>
                </svg>

                <div className="absolute" style={{ left: "56%", top: "44%", transform: "translate(-50%, -100%)" }}>
                  <span className="absolute left-1/2 bottom-0 -translate-x-1/2 w-10 h-10 rounded-full bg-[#1565C0]/30 animate-ping" />
                  <span className="relative grid place-items-center w-10 h-10 rounded-full bg-[#1565C0] text-white shadow-lg ring-4 ring-white">
                    <MapPin size={20} className="fill-white text-[#1565C0]" />
                  </span>
                </div>

                <div className="absolute bottom-3 left-3 glass-card rounded-xl px-3 py-2">
                  <div className="text-xs font-bold text-slate-900 leading-tight">Auto Valy Drive</div>
                  <div className="text-[11px] text-slate-500">Pașcani, jud. Iași</div>
                </div>

                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(SITE.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-maps-btn"
                  className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-white text-[#1565C0] text-xs font-semibold rounded-full px-3.5 py-1.5 shadow-sm hover:bg-[#1565C0] hover:text-white transition-colors"
                >
                  <MapPin size={13} /> Deschide în Maps
                </a>
              </div>
            </div>
          </Reveal>

          {/* Foaia de semnat — formularul */}
          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white rounded-[28px] border border-slate-200 p-8 lg:p-10">
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Nume complet</label>
                  <input
                    data-testid="input-nume"
                    type="text"
                    value={form.nume}
                    onChange={(e) => setForm({ ...form, nume: e.target.value })}
                    placeholder="Ex: Andrei Popescu"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#1565C0] focus:ring-2 focus:ring-[#1565C0]/20 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Număr de telefon</label>
                  <input
                    data-testid="input-telefon"
                    type="tel"
                    value={form.telefon}
                    onChange={(e) => setForm({ ...form, telefon: e.target.value })}
                    placeholder="07xx xxx xxx"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#1565C0] focus:ring-2 focus:ring-[#1565C0]/20 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Mesaj</label>
                  <textarea
                    data-testid="input-mesaj"
                    rows={4}
                    value={form.mesaj}
                    onChange={(e) => setForm({ ...form, mesaj: e.target.value })}
                    placeholder="Spune-ne cum te putem ajuta..."
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#1565C0] focus:ring-2 focus:ring-[#1565C0]/20 transition resize-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1565C0] text-white hover:bg-[#0D47A1] rounded-full px-8 py-4 font-semibold transition-all brand-shadow hover:-translate-y-0.5"
                >
                  <Send size={18} /> Trimite pe WhatsApp
                </button>
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="gdpr-consent-label">
                  <input
                    type="checkbox"
                    required
                    checked={gdpr}
                    onChange={(e) => setGdpr(e.target.checked)}
                    data-testid="gdpr-consent-checkbox"
                    className="mt-1 w-4 h-4 shrink-0 accent-[#1565C0]"
                  />
                  <span className="text-sm text-slate-500 leading-relaxed">
                    Prin trimiterea formularului sunt de acord cu{" "}
                    <Link
                      to="/politica-confidentialitate"
                      data-testid="gdpr-privacy-link"
                      className="text-[#1565C0] hover:text-[#0D47A1] underline underline-offset-2 transition-colors"
                    >
                      Politica de Confidențialitate
                    </Link>
                    .
                  </span>
                </label>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
